import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Truck, ArrowLeft } from "lucide-react";

/**
 * Shared layout for legal / policy pages (Privacy, Terms, Cookies, AUP,
 * Data Deletion, Support). Reused so we don't repeat the header / footer
 * markup across each individual page.
 *
 * Props:
 *   title          string  — page heading
 *   canonical      string  — canonical URL
 *   description    string  — <meta name="description">
 *   lastUpdated    string  — display-friendly date string
 *   testidPrefix   string  — used for `data-testid={prefix}-section-{id}`
 *   children       React   — render <Section /> children
 */
export default function LegalLayout({ title, canonical, description, lastUpdated, testidPrefix, children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>{title} — Transport Anything 247</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
      </Helmet>

      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Truck className="h-7 w-7 text-orange-500" />
            <span className="text-lg font-bold" style={{ fontFamily: "var(--font-heading)" }}>
              Transport Anything 247
            </span>
          </Link>
          <Link to="/" className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
        </div>
      </header>

      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>
            {title}
          </h1>
          {lastUpdated && (
            <p className="text-sm text-slate-500 mt-2">
              Last updated: <span className="font-medium text-slate-700" data-testid={`${testidPrefix}-last-updated`}>{lastUpdated}</span>
            </p>
          )}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        {children}
        <p className="text-xs text-slate-500 mt-12 text-center">
          © {new Date().getFullYear()} Transport Anything 247. All rights reserved.
        </p>
      </main>
    </div>
  );
}

export const Section = ({ id, title, testidPrefix = "legal", children }) => (
  <section id={id} className="scroll-mt-20 mb-10" data-testid={`${testidPrefix}-section-${id}`}>
    <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
      {title}
    </h2>
    <div className="text-[15px] text-slate-700 leading-relaxed space-y-3">{children}</div>
  </section>
);
